import dayjs from 'dayjs';

// The figures shown above the Accounts Manager's daily table on the Project Tracker: income by department and week by week.
// Pure functions over the response of /cro-project-tracker/department-income:
//   departments = meta.departments  [{ deptId, deptName, target, revenueToDate }]
//   rows        = data              [{ date, byDept: { [deptId]: amount }, total }]   one per day of the month
//   lastYear    = meta.lastYear.data                                                  the same month last year, same shape (amounts only)

/** How far through the month we are: the days that have happened (all of a past month, 0 for a future one). */
export const monthProgress = (month, now = dayjs()) => {
  const start = dayjs(month).startOf('month');
  const daysInMonth = start.daysInMonth();
  let elapsedDays = daysInMonth;
  if (start.isAfter(now, 'month')) elapsedDays = 0;
  else if (start.isSame(now, 'month')) elapsedDays = now.date();
  return { daysInMonth, elapsedDays, isCurrentMonth: start.isSame(now, 'month') };
};

const dayOf = (date) => Number(String(date).slice(8, 10));
const sumBy = (rows, pick) => rows.reduce((t, r) => t + (Number(pick(r)) || 0), 0);

// the day with the most income so far (ties: the earliest) - null when nothing was collected
const bestDayOf = (rows, pick, elapsedDays) => {
  let best = null;
  rows.forEach((r) => {
    if (dayOf(r.date) > elapsedDays) return;
    const amount = Number(pick(r)) || 0;
    if (amount > 0 && (!best || amount > best.amount)) best = { amount, date: r.date };
  });
  return best;
};

// percent change; null when there is no base to compare with
const changePct = (now, before) => (before > 0 ? ((now - before) / before) * 100 : null);

/**
 * One line per department, then the Total of the departments shown.
 *   share      the department's part of the combined income of the departments shown, so far (%)
 *   dailyAvg   income so far / days so far
 *   bestDay    the biggest single day so far { amount, date }
 *   lastYear   the SAME MONTH last year, days 1..N (N = days so far), so a part-way month is not set against a whole one
 *   change     income vs lastYear in % (null when that month had no income)
 */
export const summariseDepartments = ({ departments = [], rows = [], lastYearRows = [], elapsedDays = 0 }) => {
  const days = Math.max(elapsedDays, 1);
  const samePeriod = lastYearRows.filter((r) => dayOf(r.date) <= elapsedDays);
  const build = (income, pick, prevPick) => {
    const lastYear = sumBy(samePeriod, prevPick);
    return { income, dailyAvg: income / days, bestDay: bestDayOf(rows, pick, elapsedDays), lastYear, change: changePct(income, lastYear) };
  };
  const lines = departments.map((d) => ({ deptId: d.deptId, deptName: d.deptName, ...build(Number(d.revenueToDate) || 0, (r) => r.byDept?.[d.deptId], (r) => r.byDept?.[d.deptId]) }));
  const grand = sumBy(lines, (l) => l.income);
  lines.forEach((l) => {
    l.share = grand > 0 ? (l.income / grand) * 100 : null;
  });
  // last year's days carry only the per-department amounts (no total), so their total is the sum of those
  const dayTotal = (r) => sumBy(Object.values(r.byDept || {}), (v) => v);
  const total = { deptId: 'total', deptName: 'Total', ...build(grand, (r) => r.total, dayTotal), share: grand > 0 ? 100 : null };
  return { lines, total };
};

const WEEKS = [
  { key: 'w1', label: 'Week 1', from: 1, to: 7 },
  { key: 'w2', label: 'Week 2', from: 8, to: 14 },
  { key: 'w3', label: 'Week 3', from: 15, to: 21 },
  { key: 'w4', label: 'Week 4', from: 22, to: 31 }, // to the month end, like the sheet's 22-31
];

/**
 * Income per department for each of the sheet's four weeks (1-7, 8-14, 15-21, 22-end) plus the whole month.
 * A week that has not started yet (current month) is marked `started: false` so it can show a dash instead of a misleading zero.
 */
export const weekByWeek = ({ departments = [], rows = [], elapsedDays = 0, daysInMonth = 31 }) => {
  const make = (key, label, from, to) => {
    const inWeek = rows.filter((r) => dayOf(r.date) >= from && dayOf(r.date) <= to);
    const byDept = {};
    departments.forEach((d) => {
      byDept[d.deptId] = sumBy(inWeek, (r) => r.byDept?.[d.deptId]);
    });
    return { key, label, range: `${from}–${Math.min(to, daysInMonth)}`, started: elapsedDays >= from, byDept, total: sumBy(inWeek, (r) => r.total) };
  };
  return {
    weeks: WEEKS.map((w) => make(w.key, w.label, w.from, w.to)),
    month: make('month', 'Month', 1, daysInMonth),
  };
};

/**
 * The days with the most income so far, biggest first (ties: the earlier day), each with its share of the month's income.
 * Days with nothing collected and days that have not happened yet are left out.
 */
export const topDays = ({ rows = [], elapsedDays = 0, limit = 5 }) => {
  const monthTotal = sumBy(rows.filter((r) => dayOf(r.date) <= elapsedDays), (r) => r.total);
  return rows
    .filter((r) => dayOf(r.date) <= elapsedDays && Number(r.total) > 0)
    .map((r) => ({ date: r.date, amount: Number(r.total), share: monthTotal > 0 ? (Number(r.total) / monthTotal) * 100 : null }))
    .sort((a, b) => b.amount - a.amount || a.date.localeCompare(b.date))
    .slice(0, limit);
};
